import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IdLabel, ResultChip } from '../../components/atoms.js';
import { SidePanel } from '../../components/side-panel.js';
import { catalogApi } from '../../lib/catalog-api.js';
import { pickLang } from '../../lib/pick-lang.js';
import { type OpenDefectRef, plansApi } from '../../lib/plans-api.js';
import { RunHistory } from './run-history.js';

// Ab dem dritten offenen Defect wird gekürzt — sonst sprengt eine Kette von IDs die Spalte und
// die Tabelle wird unlesbar. Der Drill-down zeigt ohnehin alle.
const MAX_DEFECT_CHIPS = 2;

function DefectChips({ defects }: { defects: OpenDefectRef[] }) {
  const { t } = useTranslation();
  if (defects.length === 0) return <span className="text-text-muted">—</span>;
  const shown = defects.slice(0, MAX_DEFECT_CHIPS);
  const rest = defects.length - shown.length;
  return (
    <span className="flex flex-wrap items-center gap-2">
      {shown.map((d) => (
        <Link key={d.defect_id} to={`/defects/${d.defect_id}`}>
          <IdLabel id={d.human_id} />
        </Link>
      ))}
      {rest > 0 && (
        <span className="text-text-muted">{t('plans.moreDefects', { count: rest })}</span>
      )}
    </span>
  );
}

// #35: eigener Abschnitt statt nur der Spalte in der Scope-Tabelle — die Scope-Tabelle ist zum
// Konfigurieren da, hier geht es ums Nachvollziehen. Zeigt bewusst nur Testfälle, die der Katalog
// noch kennt: ein Fortschritts-Eintrag ohne Katalog-Eintrag hätte weder ID noch Titel.
export function PlanProgressSection({
  projectId,
  planId,
}: {
  projectId: string;
  planId: string;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const [openRun, setOpenRun] = useState<{ id: string; title: string } | null>(null);

  const { data: progress } = useQuery({
    queryKey: ['plan-progress', projectId, planId],
    queryFn: () => plansApi.progress(projectId, planId),
    enabled: Boolean(projectId) && Boolean(planId),
  });
  const { data: testCases } = useQuery({
    queryKey: ['catalog-testcases', projectId],
    queryFn: () => catalogApi.listTestCases(projectId),
    enabled: Boolean(projectId),
  });

  if (!progress || !testCases) {
    return <div className="text-text-muted">{t('common.loading')}</div>;
  }

  const testCaseById = new Map(testCases.map((tc) => [tc.test_case_id, tc]));
  const rows = progress.items.filter((i) => testCaseById.has(i.test_case_id));

  return (
    <div className="rounded-card border border-[var(--border)] bg-bg-surface">
      <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
        <span className="text-sm font-semibold text-text-primary">{t('plans.progressTitle')}</span>
        <span className="text-[11px] text-text-muted">
          {t('plans.coverage')}: {progress.coveragePct}% · {progress.byStatus.successful}/
          {progress.total}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-xs text-text-muted">{t('plans.noProgress')}</p>
      ) : (
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-text-muted">
              <th className="px-4 py-2">{t('plans.name')}</th>
              <th className="px-4 py-2">{t('plans.progress')}</th>
              <th className="px-4 py-2">{t('plans.runsCount')}</th>
              <th className="px-4 py-2">{t('plans.lastRun')}</th>
              <th className="px-4 py-2">{t('plans.defectsCol')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => {
              const tc = testCaseById.get(item.test_case_id);
              if (!tc) return null;
              const title = pickLang(lang, tc.title_de, tc.title_en);
              return (
                <tr key={item.test_case_id}>
                  <td className="px-4 py-2 text-text-body">
                    <button
                      type="button"
                      aria-label={`${t('plans.runHistory')}: ${title}`}
                      onClick={() => setOpenRun({ id: item.test_case_id, title })}
                      className="text-left hover:underline"
                    >
                      <IdLabel id={tc.human_id} /> {title}
                    </button>
                  </td>
                  <td className="px-4 py-2">
                    <ResultChip status={item.status} />
                  </td>
                  <td className="px-4 py-2 text-text-body">{item.runs_count}</td>
                  <td className="px-4 py-2 text-text-muted">
                    {item.last_run ? (
                      <>
                        {new Date(item.last_run.executed_at).toLocaleDateString('de-DE')} ·{' '}
                        {item.last_run.tester_name ?? t('plans.unknownTester')}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <DefectChips defects={item.open_defects} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {openRun && (
        <SidePanel
          title={`${t('plans.runHistory')} — ${openRun.title}`}
          onClose={() => setOpenRun(null)}
        >
          <RunHistory projectId={projectId} planId={planId} testCaseId={openRun.id} />
        </SidePanel>
      )}
    </div>
  );
}
